import { ApolloError, useApolloClient } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import {
    GetProjectNuggetContentViewQuery,
    GetProjectProductFamilyViewQuery,
    NuggetContentViewQuery,
    ProductFamilyViewQuery,
} from 'api/graphQL/queries/projectElements.queries'
import {
    beginNuggetContentViewFetch,
    endNuggetContentViewFetch,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { useSuggestionsStore } from 'components/WorkspaceEditor/SuggestionStore'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useElementItemShallowSelector } from '../../ElementItemStore'
import {
    GetNuggetContentViewResponse,
    GetProductFamilyViewResponse,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
    PartType,
} from '../../types/types'
import { useShallowSelector } from '../../Workspace2Store'
import { useProductFetcherUtils } from './useProductFetcher.utils'

export interface ProductFetcherProps {
    projectId?: string
    overrideContentItemNodes?: boolean
    callback?: (
        response:
            | GetProjectProductFamilyViewResponse
            | GetProjectNuggetContentViewResponse
    ) => Promise<void>
    onError?: () => void
}

export interface ContentItemQueryVariables {
    isFromSMM?: true | undefined
    nodeId?: string | undefined
    elementId?: string | undefined
    id: string
    projectId?: string
    partType: PartType
    rootArticleContentItemIds: string[] | null
    isTrackChangeToggleOn: boolean
    features?: string[]
}

export const useProductFetcher = ({
    projectId = '',
    overrideContentItemNodes = false,
    callback,
    onError,
}: ProductFetcherProps) => {
    const apolloClient = useApolloClient()
    const {
        fetchContentItem,
        handleFetchError,
        onContentViewFetchSuccess,
        buildQueryVariables,
        executeApolloQuery,
    } = useProductFetcherUtils({
        overrideContentItemNodes,
        projectId,
    })

    const isMountedRef = useRef(true)
    const abortControllerRef = useRef<AbortController | null>(null)
    const requestedPartTypeRef = useRef<PartType | null>(null)

    // cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            isMountedRef.current = false
            // Abort any ongoing queries when component unmounts
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    const {
        contentItemViewNodes,
        articlesLoading,
        setArticlesLoading,
        setIsTreeLoading,
        activeTab,
        isContentTeamFromStudioPage,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        articlesLoading: state.articlesLoading,
        setArticlesLoading: state.actions.setArticlesLoading,
        setIsTreeLoading: state.actions.setIsTreeLoading,
        activeTab: state.activeTab,
        isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
    }))
    const {
        elementId,
        isProjectProductFamily: isPPF,
        id: nodeId,
        isProductTypeProject,
    } = useElementItemShallowSelector((s) => s.elementItemValue)
    const setIsSuggestionsDataUpdated = useSuggestionsStore(
        (s) => s.setIsSuggestionsDataUpdated
    )
    const projectIdFromUrl = useParams<{ projectId?: string }>().projectId

    const finishLoading = useCallback(
        (isFamily?: boolean) => {
            setIsSuggestionsDataUpdated(!isFamily)
            setIsTreeLoading(false)
        },
        [setIsSuggestionsDataUpdated, setIsTreeLoading]
    )

    const executionContext = useMemo(
        () => ({
            apolloClient,
            callback,
            onContentViewFetchSuccess,
            articlesLoading,
            finishLoading,
            handleFetchError,
            setArticlesLoading,
            onError,
        }),
        [
            apolloClient,
            callback,
            onContentViewFetchSuccess,
            articlesLoading,
            finishLoading,
            handleFetchError,
            setArticlesLoading,
            onError,
        ]
    )

    const [getProjectProductFamilyView] = useLazyQuery<
        GetProjectProductFamilyViewResponse
    >(GetProjectProductFamilyViewQuery(6), {
        fetchPolicy: 'no-cache',
        onCompleted: async (response: GetProjectProductFamilyViewResponse) => {
            if (!isMountedRef.current) return

            if (callback) {
                await callback(response)
            } else {
                onContentViewFetchSuccess(
                    response,
                    articlesLoading,
                    false,
                    null,
                    requestedPartTypeRef.current || undefined
                ).then(() => {
                    if (isMountedRef.current) {
                        finishLoading()
                    }
                })
            }
        },
        onError: (error: ApolloError) => {
            if (!isMountedRef.current) return

            console.error('Failed to fetch project product family:', {
                component: 'useProductFetcher',
                operation: 'getProjectProductFamilyView',
                error: error.message,
            })

            handleFetchError(error, setArticlesLoading, onError)
            finishLoading()
        },
    })

    // Family queries without callback support
    const [getProductFamilyView] = useLazyQuery<GetProductFamilyViewResponse>(
        ProductFamilyViewQuery(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: (response: GetProductFamilyViewResponse) => {
                if (!isMountedRef.current) return

                onContentViewFetchSuccess(
                    response,
                    articlesLoading,
                    false,
                    null,
                    requestedPartTypeRef.current || undefined
                ).then(() => {
                    if (isMountedRef.current) {
                        finishLoading(true)
                    }
                })
            },
            onError: (error) => {
                if (!isMountedRef.current) return

                handleFetchError(error, setArticlesLoading, onError)
                finishLoading(true)
            },
        }
    )

    const [
        getProjectProductTypeView,
        { loading: projectProductTypeViewLoading },
    ] = useLazyQuery<
        GetProjectNuggetContentViewResponse
    >(GetProjectNuggetContentViewQuery(6), {
        fetchPolicy: 'no-cache',
        onCompleted: async (response: GetProjectNuggetContentViewResponse) => {
            if (!isMountedRef.current) return

            if (callback) {
                await callback(response)
            } else {
                onContentViewFetchSuccess(
                    response,
                    articlesLoading,
                    false,
                    null,
                    requestedPartTypeRef.current || undefined
                ).then(() => {
                    if (isMountedRef.current) {
                        finishLoading()
                    }
                })
            }
        },
        onError: (error: ApolloError) => {
            if (!isMountedRef.current) return

            handleFetchError(error, setArticlesLoading, onError)
            finishLoading()
        },
    })

    // Track in-flight state so Remove button can block until this query settles
    useEffect(() => {
        if (!projectProductTypeViewLoading) return
        beginNuggetContentViewFetch()
        return endNuggetContentViewFetch
    }, [projectProductTypeViewLoading])

    const [getProductTypeView] = useLazyQuery<GetNuggetContentViewResponse>(
        NuggetContentViewQuery(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: (response: GetNuggetContentViewResponse) => {
                if (!isMountedRef.current) return

                onContentViewFetchSuccess(
                    response,
                    articlesLoading,
                    false,
                    null,
                    requestedPartTypeRef.current || undefined
                ).then(() => {
                    if (isMountedRef.current) {
                        finishLoading(true)
                    }
                })
            },
            onError: (error) => {
                if (!isMountedRef.current) return

                handleFetchError(error, setArticlesLoading, onError)
                finishLoading(true)
            },
        }
    )

    const fetchFamilyContentItemView = useCallback(
        async (
            partType: PartType,
            rootArticleContentItemIds?: string[]
        ): Promise<void> => {
            // Cancel previous query if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
            abortControllerRef.current = new AbortController()

            requestedPartTypeRef.current = partType

            const loadProduct = isPPF
                ? getProjectProductFamilyView
                : getProductFamilyView
            return fetchContentItem({
                partType,
                loadProduct,
                rootArticleContentItemIds,
            })
        },
        [
            isPPF,
            getProjectProductFamilyView,
            getProductFamilyView,
            fetchContentItem,
        ]
    )

    const fetchProductTypeContentItemView = useCallback(
        async (
            partType: PartType,
            rootArticleContentItemIds?: string[]
        ): Promise<void> => {
            // Cancel previous query if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
            abortControllerRef.current = new AbortController()

            requestedPartTypeRef.current = partType

            const loadProduct = isPPF
                ? getProjectProductTypeView
                : getProductTypeView
            return fetchContentItem({
                partType,
                loadProduct,
                rootArticleContentItemIds,
            })
        },
        [isPPF, getProjectProductTypeView, getProductTypeView, fetchContentItem]
    )

    const buildQueryVariablesCallback = useCallback(
        (
            rootArticleContentItemIds: string[] | null,
            activeTabIndex?: number
        ): ContentItemQueryVariables =>
            buildQueryVariables({
                elementId,
                projectId,
                projectIdFromUrl,
                activeTab,
                isTrackChangeToggleOn: true,
                isPPF,
                nodeId,
                isFromSMM: isContentTeamFromStudioPage,
                rootArticleContentItemIds,
                activeTabIndex,
            }),
        [
            buildQueryVariables,
            elementId,
            contentItemViewNodes,
            projectId,
            projectIdFromUrl,
            activeTab,
            isPPF,
            nodeId,
            isContentTeamFromStudioPage,
        ]
    )

    const getContentItemViewNodesAPI = useCallback(
        async (
            rootArticleContentItemIds: string[] | null,
            activeTabIndex?: number,
            overrideElementId?: string,
            overrideNodeId?: string
        ): Promise<void> => {
            const loadProduct = isProductTypeProject
                ? getProjectProductTypeView
                : getProjectProductFamilyView

            let queryVariables = buildQueryVariablesCallback(
                rootArticleContentItemIds,
                activeTabIndex
            )

            // Override elementId if provided
            if (overrideElementId) {
                queryVariables = {
                    ...queryVariables,
                    elementId: overrideElementId,
                }
            }

            // Override nodeId if provided
            if (overrideNodeId) {
                queryVariables = {
                    ...queryVariables,
                    nodeId: overrideNodeId,
                }
            }

            return loadProduct({ variables: queryVariables })
        },
        [
            isProductTypeProject,
            getProjectProductTypeView,
            getProjectProductFamilyView,
            buildQueryVariablesCallback,
        ]
    )

    const getContentItemViewNodesWithApolloClient = useCallback(
        async (
            rootArticleContentItemIds: string[] | null,
            activeTabIndex?: number
        ): Promise<void> => {
            const queryVariables = buildQueryVariablesCallback(
                rootArticleContentItemIds,
                activeTabIndex
            )
            const query = isProductTypeProject
                ? GetProjectNuggetContentViewQuery(6)
                : GetProjectProductFamilyViewQuery(6)

            await executeApolloQuery({
                ...executionContext,
                query,
                queryVariables,
            })
        },
        [buildQueryVariablesCallback, isProductTypeProject, executionContext]
    )

    return {
        fetchFamilyContentItemView,
        fetchProductTypeContentItemView,
        getContentItemViewNodesAPI,
        getContentItemViewNodesWithApolloClient,
        onContentViewFetchSuccess,
    }
}
